import * as React from 'react';
import { cn } from '../utils/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean;
  leftSlot?: React.ReactNode;
  rightSlot?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', invalid, leftSlot, rightSlot, ...props }, ref) => {
    if (leftSlot || rightSlot) {
      return (
        <div
          className={cn(
            'flex h-11 items-center gap-2 rounded-full border bg-white px-4',
            'transition-colors duration-200',
            invalid
              ? 'border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-200'
              : 'border-[var(--color-border-strong)] focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-100',
            className,
          )}
        >
          {leftSlot ? <span className="text-[var(--color-foreground-subtle)]">{leftSlot}</span> : null}
          <input
            ref={ref}
            type={type}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--color-foreground-subtle)]"
            aria-invalid={invalid || undefined}
            {...props}
          />
          {rightSlot ? <span className="text-[var(--color-foreground-subtle)]">{rightSlot}</span> : null}
        </div>
      );
    }
    return (
      <input
        ref={ref}
        type={type}
        aria-invalid={invalid || undefined}
        className={cn(
          'h-11 w-full rounded-full border bg-white px-4 text-sm',
          'placeholder:text-[var(--color-foreground-subtle)]',
          'transition-colors duration-200 outline-none',
          invalid
            ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-200'
            : 'border-[var(--color-border-strong)] focus:border-brand-500 focus:ring-2 focus:ring-brand-100',
          'disabled:cursor-not-allowed disabled:bg-[var(--color-surface-subtle)] disabled:opacity-60',
          className,
        )}
        {...props}
      />
    );
  },
);
Input.displayName = 'Input';

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, invalid, ...props }, ref) => (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'min-h-24 w-full rounded-2xl border bg-white px-4 py-3 text-sm',
        'placeholder:text-[var(--color-foreground-subtle)]',
        'transition-colors duration-200 outline-none resize-y',
        invalid
          ? 'border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-200'
          : 'border-[var(--color-border-strong)] focus:border-brand-500 focus:ring-2 focus:ring-brand-100',
        className,
      )}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';
